import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Loading from "../Layout/Loading";
import EntryFormContent from "./EntryFormContent";
import { getUserDetailsForApplicationSale } from "../../services/applicationReport";
import { getUserId } from "../../Common/functions";

const MAX_MOBILE_SCREEEN_SIZE = process.env.REACT_APP_MAX_MOBILE_SCREEN_SIZE;
const SUBMISSION_COOLDOWN = 15000;

const EditEntry = ({
  userRole,
  closeModal: parentCloseModal,
  selectedObject: editData,
  onUpdateSuccess,
}) => {
  const modalRef = useRef(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(0);
  const [userProfileDetails, setUserProfileDetails] = useState({});

  const [productGroupList, setProductGroupList] = useState([]);
  const [productNameOptions, setProductNameOptions] = useState([]);
  const [doctorsNameList, setDoctorsNameList] = useState([]);

  const [formData, setFormData] = useState({
    product_id: "",
    product_group: "",
    doctorsname: "",
    quantity: "",
    tracking_status: "Lead",
    patientname: "",
    patientemail: "",
    patientnumber: "",
    dates: "",
    distributor_name: "",
    product_code: "",
    unit_price: "",
    points: "",
    // ...editData, // Populate with existing data
  });
  const [loading, setLoading] = useState(false);

  // Check if the screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MAX_MOBILE_SCREEEN_SIZE);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Initialize form data once when component mounts
  useEffect(() => {
    if (editData) {
      setFormData(editData);
    }
  }, []);

  const getUserProfileDetails = async () => {
    let result = [];
    result = await getUserDetailsForApplicationSale({
      category:
        userRole === "super_admin" || userRole === "application_specialist"
          ? "user-profile"
          : userRole === "distributor_admin"
          ? "distributor-admin-profile"
          : "Internal-user-profile",
      UserId: getUserId(),
    });
    console.log("re", result);
    setUserProfileDetails(result?.data[0]);
  };

  useEffect(() => {
    getUserProfileDetails();
  }, []);

  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => {
      parentCloseModal();
    }, 400);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    console.log(`Field changed: ${name} = ${value}`);

    // Use functional update to ensure we always have the latest state
    setFormData((prevFormData) => {
      if (name === "product_group") {
        const selectedGroup = productGroupList.find(
          (group) => group.group_name === value
        );
        return {
          ...prevFormData,
          product_group: value,
          product_name: selectedGroup ? selectedGroup.group_id : "",
          product_id: "",
          product_code: "",
          unit_price: "",
        };
      } else if (name === "product_id") {
        const selectedProduct = productNameOptions.find(
          (item) => item.item_id === value
        );
        return {
          ...prevFormData,
          [name]: value,
          product_code: selectedProduct ? selectedProduct.name : "",
          unit_price: selectedProduct ? selectedProduct.rate : "",
        };
      } else if (name === "doctorsname") {
        const selectedDoctor = doctorsNameList.find(
          (doctor) => doctor.Full_Name === value
        );
        return {
          ...prevFormData,
          [name]: value,
          doctors_name: selectedDoctor ? selectedDoctor.id : "",
        };
      } else if (name === "tracking_status") {
        // Set points based on tracking status
        let points = "";
        if (value === "Lead") points = "10";
        else if (value === "Demo") points = "20";
        else if (value === "Sale") points = "30";

        return {
          ...prevFormData,
          [name]: value,
          points: points,
        };
      } else {
        return {
          ...prevFormData,
          [name]: value,
        };
      }
    });
  };

  // Comprehensive form validation
  const validateForm = () => {
    const {
      product_group,
      quantity,
      doctorsname,
      tracking_status,
      product_id,
      patientname,
      dates,
    } = formData;

    // Required fields validation
    const requiredFieldsEmpty = [];

    if (!product_group) requiredFieldsEmpty.push("Product group");
    if (!quantity) requiredFieldsEmpty.push("Quantity");
    if (!doctorsname) requiredFieldsEmpty.push("Doctors name");
    if (!patientname) requiredFieldsEmpty.push("Patient name");
    if (!dates) requiredFieldsEmpty.push("Date");

    // Product name validation for Sale/NoSale tracking_status
    if (
      (tracking_status === "Sale" || tracking_status === "NoSale") &&
      !product_id
    ) {
      requiredFieldsEmpty.push("Product name");
    }

    if (requiredFieldsEmpty.length > 0) {
      const errorMessage =
        requiredFieldsEmpty.join(", ") +
        (requiredFieldsEmpty.length === 1 ? " is empty" : " are empty");
      toast.error(errorMessage);
      return false;
    }

    // Additional validation for Sale/NoSale - product code must be selected
    if (
      (tracking_status === "Sale" || tracking_status === "NoSale") &&
      (!formData.product_id || formData.product_id === "")
    ) {
      toast.error("Select product name");
      return false;
    }

    return true;
  };

  // Prepare data for submission
  const prepareSubmissionData = () => {
    const data = {
      ...formData,
      UserId: getUserId(),
      distributor_name:
        userProfileDetails?.distributor_name || formData.distributor_name,
      username: userProfileDetails?.username || "",
      category: "sales tracking",
      nickname: userProfileDetails?.nickname || "",
      // product_name: editData ? editData.product_name : "",
    };

    // Remove product_name from data as per jQuery logic      // FIX THIS IMPORTANT
    // delete data.product_name;

    // Handle product code and ID based on tracking_status
    const selectedValue = formData.tracking_status;

    if (selectedValue === "Lead") {
      // For Lead, include product_code and product_id
      data.product_code = formData.product_code;
      data.product_id = formData.product_id;
    } else if (selectedValue === "Demo") {
      // For Demo, include product_code and product_id
      data.product_code = formData.product_code;
      data.product_id = formData.product_id;
    } else if (selectedValue === "Sale" || selectedValue === "NoSale") {
      // For Sale/NoSale, ensure product_code and product_id are included
      data.product_code = formData.product_code;
      data.product_id = formData.product_id;
    }

    return data;
  };

  // Update sales tracking record
  const updateSalesTracking = async (data) => {
    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/sales_tracking/sales_trcking_update",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      return await response.json();
    } catch (error) {
      console.error("Error updating sales tracking:", error);
      throw error;
    }
  };

  // Update CRM record
  const updateCRMRecord = async (data) => {
    const crmData = {
      ...data,
      id: editData.record_id, // Use the record_id from editData
    };

    const crmFormat = JSON.stringify({ data: [crmData] });

    try {
      const response = await fetch(
        "https://46zd64zt92.execute-api.us-east-2.amazonaws.com/Prod/Update_custom_module",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: crmFormat,
        }
      );

      return await response.json();
    } catch (error) {
      console.error("Error updating CRM record:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    console.log("Hii");
    // Check submission cooldown
    const currentTime = new Date().getTime();
    if (currentTime - lastSubmitted < SUBMISSION_COOLDOWN) {
      toast.warning("Please wait before submitting again");
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Update last submission time
      setLastSubmitted(currentTime);

      // Prepare data
      const submissionData = prepareSubmissionData();
      console.log("Submission data:", submissionData);

      // Update sales tracking record
      const salesTrackingResponse = await updateSalesTracking(submissionData);
      console.log("Sales tracking response:", salesTrackingResponse);

      // Update CRM record
      const crmResponse = await updateCRMRecord(submissionData);
      console.log("CRM response:", crmResponse);

      toast.success("Successfully updated");

      // Call success callback if provided
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      // Close modal
      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Error occurred during update");
    } finally {
      setLoading(false);
    }
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <AnimatePresence>
        {isOpen &&
          (isMobile ? (
            // Mobile dialog (centered, no animation)
            <div
              ref={modalRef}
              className="bg-white w-[90%] max-w-md rounded-lg max-h-[70vh] overflow-y-auto px-6 shadow-lg"
            >
              {loading ? (
                <Loading />
              ) : (
                <>
                  <EntryFormContent
                    formData={formData}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    handleCancel={parentCloseModal}
                    heading="Edit Entry"
                    productGroupList={productGroupList}
                    setProductGroupList={setProductGroupList}
                    productNameOptions={productNameOptions}
                    setProductNameOptions={setProductNameOptions}
                    doctorsNameList={doctorsNameList}
                    setDoctorsNameList={setDoctorsNameList}
                    isEdit={true}
                  />
                </>
              )}
            </div>
          ) : (
            // Desktop slide-in panel
            <motion.div
              ref={modalRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.4 }}
              className="bg-white w-[70vw] h-full px-6 absolute right-0 top-0 shadow-lg max-w-[900px]"
            >
              {loading ? (
                <Loading />
              ) : (
                <>
                  <button
                    onClick={closeModal}
                    className="absolute top-0 -left-12 bg-gray-200 rounded-md p-1 shadow-md"
                  >
                    <X className="w-7 h-7 text-gray-500 hover:text-gray-800" />
                  </button>
                  <div className="pt-4">
                    <EntryFormContent
                      formData={formData}
                      handleChange={handleChange}
                      handleSubmit={handleSubmit}
                      heading="Edit Entry"
                      productGroupList={productGroupList}
                      setProductGroupList={setProductGroupList}
                      productNameOptions={productNameOptions}
                      setProductNameOptions={setProductNameOptions}
                      doctorsNameList={doctorsNameList}
                      setDoctorsNameList={setDoctorsNameList}
                      isEdit={true}
                    />
                  </div>
                </>
              )}
            </motion.div>
          ))}
      </AnimatePresence>
    </div>
  );
};

export default EditEntry;
